/**
 * Metric Help Content Registry (Iter 76.15)
 * ------------------------------------------
 * Structured help content for every user-facing KPI/metric across
 * executive dashboards, the Compliance Center, and the Backup module.
 *
 * FUTURE EXTENSIBILITY:
 * All lookups go through `getMetricHelp(topicKey)`. To externalize this
 * to a CMS or backend content service later, swap the body of that
 * function for a fetched-once/cached lookup — no consumer changes.
 *
 * CONTENT SHAPE:
 *   {
 *     title: string,                    // Bolded heading
 *     what: string,                     // What this metric represents
 *     why: string,                      // Why it matters
 *     how: string | string[],           // How the value is calculated (bullets ok)
 *     affects: string[],                // Bullets: what raises/lowers the score
 *     recommendedActions: string[],     // Bullets: when the value is low/warning
 *     cadence: string,                  // "Real-time", "Every 15s", "Daily 04:30 UTC", etc.
 *   }
 */

const REGISTRY = {
  // ─────────────── Overall Compliance ──────────────────────────────────
  compliance_score: {
    title: "Overall Compliance Score",
    what: "A single number (0–100) representing your organization's compliance posture across every enabled regulatory framework and internal control category.",
    why: "Provides a top-line indicator for executives and board reporting. Trends over time reveal whether controls are strengthening or drifting.",
    how: "Weighted average of every active category (Coverage, Job Health, Legal Hold Hygiene, Audit Completeness, Backup Reconciliation, Exception Management, Predictive Compliance). Weights are configurable in Compliance Settings.",
    affects: [
      "Category scores below target lower the overall value.",
      "Open critical or high-severity issues drag the score down proportionally to the category's weight.",
      "Fully resolved issues restore the score on the next scan.",
    ],
    recommendedActions: [
      "Review the lowest-scoring category on the dashboard.",
      "Open outstanding findings, sort by severity, and remediate critical issues first.",
      "Enable auto-fix for safe issues to accelerate recovery.",
    ],
    cadence: "Recomputed on every scan (manual or scheduled daily at 04:30 UTC).",
  },

  compliance_grade: {
    title: "Compliance Letter Grade",
    what: "A human-readable grade (A+ through F) derived from the overall compliance score.",
    why: "Executives and board members often prefer a grade at a glance to a numeric score.",
    how: "A+ = 95+, A = 90–94, B+ = 85–89, B = 80–84, C = 70–79, D = 60–69, F = below 60.",
    affects: [
      "Any change to the overall score can shift the grade at a boundary.",
    ],
    recommendedActions: [
      "If the grade drops below B, review the lowest-scoring category immediately.",
    ],
    cadence: "Recomputed on every scan.",
  },

  // ─────────────── Compliance Categories ───────────────────────────────
  category_coverage: {
    title: "Coverage Validation",
    what: "Confirms every classified data collection has the six required governance signals: classification, retention policy, backup, audit trail, encryption at rest, and RBAC binding.",
    why: "Missing signals mean sensitive data may be unprotected or unmanaged, creating audit and regulatory risk.",
    how: "Score = 100 × (1 − failed_weight / total_weight), where total_weight is classified_collection_count × 6.",
    affects: [
      "Every classified collection missing any of the six signals lowers the score.",
      "PHI/PII/financial/security collections weigh heavier.",
    ],
    recommendedActions: [
      "Configure retention policies for uncovered record types.",
      "Ensure at least one successful backup exists.",
      "Verify RBAC permission keys cover every module.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  category_job_health: {
    title: "Job Health",
    what: "Measures whether every scheduled maintenance job runs on cadence with a high success rate and acceptable latency.",
    why: "Silent job failures let stale data, un-purged records, or missing indexes accumulate — a common source of audit findings.",
    how: "Four sub-metrics per job: cadence adherence, success rate, run duration, and last-run recency. Score = 100 × (1 − demerit / (jobs × 4)).",
    affects: [
      "Jobs that haven't run in the expected window lower the score.",
      "Jobs with recent failures or slow runs incur demerits.",
    ],
    recommendedActions: [
      "Open Maintenance and re-run any failed jobs.",
      "Check scheduler health at /api/health/deep.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  category_legal_hold: {
    title: "Legal Hold Hygiene",
    what: "Detects expired, duplicate, orphaned, unattributed, or unjustified legal holds and confirms held records are preserved while released records purge on schedule.",
    why: "Improper legal holds create legal exposure — either data is destroyed when it must be preserved, or personal data is retained past its lawful purpose.",
    how: "Each active hold contributes one unit. Any anomaly (expired, orphaned, duplicated, unattributed) is a finding proportional to its severity.",
    affects: [
      "Expired holds not released lower the score.",
      "Holds without a documented reason or approver contribute findings.",
    ],
    recommendedActions: [
      "Open Legal Holds and review any anomalies flagged in the dashboard.",
      "Release expired holds after final review.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  category_audit: {
    title: "Audit Completeness",
    what: "Confirms every sensitive action (login, MFA, PHI access, edit, export, delete, policy change, role change, retention change, legal hold change) is captured in the audit log.",
    why: "Missing audit events break the chain of custody required by HIPAA §164.312(b), SOC 2 CC7, and 42 CFR Part 2.",
    how: "The scan looks for at least one instance of every required event key (or its aliases) in the audit log over the last 30 days.",
    affects: [
      "Missing 'required' events (PHI record view, login, user management) lower the score.",
      "Missing 'informational' events surface as low-severity findings only.",
    ],
    recommendedActions: [
      "Ensure PHI-viewing endpoints call the record_view audit helper.",
      "Confirm user-management endpoints log_audit with category='user_management'.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  category_backup: {
    title: "Backup Reconciliation",
    what: "Validates that recent backups exist, are integrity-verified (SHA-256), and are within the expected cadence window.",
    why: "A backup you can't restore isn't a backup. Verification and freshness are what turn backups into a real recovery capability.",
    how: "Five sub-checks: config present, at least one successful run, latest within the freshness window, latest is hash-verified, and no unresolved failure after last success.",
    affects: [
      "Never having run a successful backup drags the score to zero.",
      "Stale, unverified, or hash-mismatched artifacts contribute findings.",
    ],
    recommendedActions: [
      "Open Backup & Disaster Recovery and click Run Backup Now.",
      "Verify the latest artifact via the Verify button on each run.",
      "Configure a daily cadence for production.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  category_exceptions: {
    title: "Exception Management",
    what: "Tracks whether every documented compliance exception has severity, owner, root cause, remediation status, due date, SLA, and an audit history.",
    why: "Exceptions without governance become 'known unknowns' — the exact class of finding that concerns auditors most.",
    how: "Each open exception must have all seven governance fields populated to count as fully managed. Missing fields incur findings proportional to severity.",
    affects: [
      "Open exceptions past their due date lower the score.",
      "Exceptions missing owner or root cause contribute findings.",
    ],
    recommendedActions: [
      "Open Exceptions and complete governance fields on every open row.",
      "Close resolved exceptions to remove them from active accounting.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  category_predictive: {
    title: "Predictive Compliance",
    what: "Forward-looking signals that flag upcoming risk — storage capacity, expiring holds, expiring policies, backup failures, deletion anomalies.",
    why: "Reactive compliance means finding out too late. Predictive signals surface issues before they become findings.",
    how: "Currently informational (0-weight in scoring). Findings appear here when trend analysis detects a leading indicator.",
    affects: [
      "Does not affect the overall score today.",
      "Findings surface as informational cues on the dashboard.",
    ],
    recommendedActions: [
      "Review any predictive findings weekly.",
      "Address root causes before they turn into hard failures.",
    ],
    cadence: "Recomputed on every compliance scan.",
  },

  // ─────────────── Executive KPI Hero ──────────────────────────────────
  organization_health: {
    title: "Organization Health",
    what: "Composite view of nine operational health signals: referral performance, community outcomes, program capacity, staff utilization, volunteer engagement, partner responsiveness, security posture, compliance status, and community intelligence.",
    why: "A single vital-sign for executives that consolidates operational, security, and compliance health into one number.",
    how: "Weighted average of the nine signal scores, each derived from live data. Grade follows the same A+…F scale as compliance.",
    affects: [
      "Any of the nine underlying signals dropping into warning or critical state lowers the composite.",
    ],
    recommendedActions: [
      "Open Executive Operations Center to identify the weakest signal.",
      "Drill into the underlying module (Referrals, Volunteers, Security Center, etc.) to remediate.",
    ],
    cadence: "Recomputed on Executive Dashboard load and every 15 seconds via the Operations Center live stream.",
  },

  active_alerts: {
    title: "Active Alerts",
    what: "Count of unacknowledged operational, security, and compliance alerts currently active across the platform.",
    why: "Executives need to know at a glance whether the platform is quiet or demanding attention.",
    how: "Sum of open alerts across Community Intelligence, Security Center, Compliance Center, and system health monitors.",
    affects: [
      "New alerts arriving increases the count.",
      "Acknowledging or resolving an alert reduces the count.",
    ],
    recommendedActions: [
      "Open Alerts Center to triage severity: Critical → High → Medium.",
      "Deep-link into the underlying event to acknowledge or resolve.",
    ],
    cadence: "Real-time (updates via SSE with polling fallback).",
  },

  compliance_snapshot: {
    title: "Compliance Snapshot",
    what: "The latest overall compliance score with grade and delta versus the prior scan.",
    why: "A pulse check that answers 'are we in compliance right now?' without opening the full dashboard.",
    how: "Reads the most recent compliance_scans document. Delta is the score change since the previous scan.",
    affects: [
      "Any change to the underlying compliance score reflects here.",
    ],
    recommendedActions: [
      "If the delta is negative, click into the Compliance Center to see which category slipped.",
    ],
    cadence: "Every scan (manual or scheduled daily at 04:30 UTC).",
  },

  // ─────────────── Backup KPIs ─────────────────────────────────────────
  backup_last_success: {
    title: "Last Successful Backup",
    what: "Timestamp of the most recent successful backup artifact on record.",
    why: "Recovery point objective (RPO) starts here. A stale value means recent data can't be restored.",
    how: "Latest row in backup_runs where status='success'.",
    affects: [
      "Running a new backup updates this value.",
      "Failed backup runs do not update it.",
    ],
    recommendedActions: [
      "If the value is older than your configured cadence, click Run Backup Now.",
      "Investigate why the scheduled job did not run.",
    ],
    cadence: "Updates on each successful backup run.",
  },

  backup_latest_size: {
    title: "Latest Artifact Size",
    what: "On-disk size of the most recent backup artifact (compressed and optionally encrypted).",
    why: "Sudden size changes often signal a data anomaly — mass insertion, mass deletion, or a schema change.",
    how: "Byte length of the .tar.gz (or .tar.gz.enc) file at the artifact path.",
    affects: [
      "Growing data volume increases size.",
      "Retention purges reduce size.",
    ],
    recommendedActions: [
      "If size jumps unexpectedly, inspect the doc_counts on the run for the anomalous collection.",
    ],
    cadence: "Updates on each successful backup run.",
  },

  backup_success_rate: {
    title: "Success Rate (30 days)",
    what: "Percentage of backup runs that completed successfully in the last 30 days.",
    why: "A single failed backup is manageable. A trend of failures is a recovery-capability crisis.",
    how: "successful_runs / total_runs × 100, over a 30-day window.",
    affects: [
      "Failed runs lower the percentage.",
      "Successful runs restore it.",
    ],
    recommendedActions: [
      "Investigate failed runs in the Backup History table.",
      "Check backend logs for the failure reason.",
    ],
    cadence: "Recomputed on every load of the Backup & DR page.",
  },

  backup_configuration: {
    title: "Backup Configuration",
    what: "Current cadence, retention window, offsite target, and encryption state.",
    why: "Ensures the configuration matches the organization's RTO/RPO commitments.",
    how: "Read from the singleton backup_config document.",
    affects: [
      "Any change made in the Configuration form updates this card.",
    ],
    recommendedActions: [
      "For production, use daily cadence, 30+ day retention, and S3 offsite target.",
    ],
    cadence: "Updates immediately on Configuration Save.",
  },

  // ─────────────── Operations Center Widgets ───────────────────────────
  operations_platform_health: {
    title: "Platform Health",
    what: "Real-time uptime, error rate, and latency of the HarborWell Connect API layer.",
    why: "Early-warning signal for production incidents before users notice degradation.",
    how: "Derived from request logs, /health/deep probes, and background job telemetry.",
    affects: [
      "5xx errors or job failures degrade the status.",
      "High latency for 5+ minutes triggers warnings.",
    ],
    recommendedActions: [
      "Open the deep-link to Security Center or backend logs.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_security_posture: {
    title: "Security Posture",
    what: "Consolidated view of authentication anomalies, brute-force lockouts, MFA adoption, and open security incidents.",
    why: "A rising number of failed logins or open security incidents often precedes a full breach attempt.",
    how: "Aggregated from login_attempts, security_incidents, and mfa_devices collections.",
    affects: [
      "Multiple lockouts in the last hour raise the status.",
      "Open critical security incidents drive the widget to critical.",
    ],
    recommendedActions: [
      "Open Security Center to review anomalies.",
      "Unlock legitimate users and investigate suspicious accounts.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_compliance_status: {
    title: "Compliance Status",
    what: "Live snapshot of the overall compliance score, most recent scan timestamp, and the count of open critical findings.",
    why: "Ensures compliance drift is visible on the executive command center — not just in the Compliance Center.",
    how: "Reads the latest compliance_scans document plus a count of open high/critical findings.",
    affects: [
      "A new lower score reduces the widget's status.",
      "Resolving critical findings restores the widget to healthy.",
    ],
    recommendedActions: [
      "Open Compliance Center and triage the highest-severity findings first.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_email_infrastructure: {
    title: "Email Infrastructure",
    what: "Deliverability metrics: bounce rate, complaint rate, template health, and current sending domain reputation.",
    why: "Undelivered notifications break care coordination — this widget catches the problem before care seekers do.",
    how: "Reads recent email_events (Resend webhooks), template health tests, and DNS DKIM/SPF checks.",
    affects: [
      "Rising bounce or complaint rates degrade the widget.",
      "Failed DNS checks push the status to critical.",
    ],
    recommendedActions: [
      "Open Email Infrastructure page to inspect the most recent bounces.",
      "Re-verify DNS records if the check is failing.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_partner_activity: {
    title: "Partner Activity",
    what: "Recent partner logins, API calls, webhook deliveries, and referral inflow from external community partners.",
    why: "Confirms partner integrations are actively used and delivering value.",
    how: "Aggregated from partner_sessions, api_call_logs, and webhook_deliveries.",
    affects: [
      "A partner going silent for 24+ hours degrades the status.",
      "Webhook delivery failures push the widget to warning.",
    ],
    recommendedActions: [
      "Open Partners and check each org's Overview page for recent activity.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_api_health: {
    title: "API Health",
    what: "Request throughput, error rates, and p95 latency of the HarborWell Connect API.",
    why: "Provides early warning for degradations before they affect care workflows.",
    how: "Derived from request logs and background middleware timing.",
    affects: [
      "5xx errors or latency spikes degrade the status.",
    ],
    recommendedActions: [
      "Check backend logs and scaling.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_background_jobs: {
    title: "Background Jobs",
    what: "Health of scheduled maintenance, compliance scans, retention runs, and notification digests.",
    why: "Silent job failures are a common source of compliance and operational drift.",
    how: "Reads scheduled_job_runs collection over the last 24 hours.",
    affects: [
      "Any job that missed its cadence degrades the widget.",
    ],
    recommendedActions: [
      "Open Maintenance and re-run failed jobs.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_system_alerts: {
    title: "System Alerts",
    what: "Count and severity of active operational alerts across every module.",
    why: "One place to see whether the platform is quiet or under active attention.",
    how: "Sums open alerts across Community Intelligence, Security Center, Compliance Center, and health monitors.",
    affects: [
      "New alerts arriving raise the count and status.",
    ],
    recommendedActions: [
      "Open Alerts Center and triage in severity order.",
    ],
    cadence: "Real-time (SSE with polling fallback).",
  },

  operations_tenant_health: {
    title: "Tenant Health",
    what: "Multi-tenant readiness indicators: partner sub-tenant activity, per-tenant RBAC integrity, and quota usage.",
    why: "Confirms tenant isolation and quotas are healthy across every partner organization.",
    how: "Aggregated from partner_orgs, tenant_usage, and RBAC audit trail.",
    affects: [
      "A tenant exceeding a quota or breaching isolation lowers the status.",
    ],
    recommendedActions: [
      "Open Partners and review any tenant flagged in warning.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_operational_kpis: {
    title: "Operational KPIs",
    what: "Consolidated view of platform-wide throughput KPIs: intakes today, active care seekers, open referrals, and volunteer utilisation.",
    why: "One glance at whether the day-to-day operation is running at expected volume.",
    how: "Aggregated live from the corresponding modules.",
    affects: [
      "A significant drop in volume relative to the trailing 7-day average degrades the widget.",
    ],
    recommendedActions: [
      "Drill into the underlying module to investigate.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_audit_summary: {
    title: "Audit Summary",
    what: "Rolling 24-hour count of audit events by category and top actors.",
    why: "Provides a chain-of-custody snapshot for compliance officers and the CISO.",
    how: "Aggregated from audit_log over the last 24 hours grouped by category.",
    affects: [
      "Anomalous spikes in a single category can raise the status for investigation.",
    ],
    recommendedActions: [
      "Open Audit Trail to inspect a specific actor or category.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },

  operations_executive_metrics: {
    title: "Executive Metrics",
    what: "Board-ready roll-up: care seekers served, referrals completed, program outcomes achieved, and community impact index.",
    why: "Answers the 'what are we accomplishing?' question in one widget.",
    how: "Aggregated from care_seekers, referrals, outcomes, and impact_events.",
    affects: [
      "Meaningful drops versus trailing month raise a warning for review.",
    ],
    recommendedActions: [
      "Drill into Program Outcomes or Impact for the underlying detail.",
    ],
    cadence: "Every 15 seconds via SSE.",
  },
};

/**
 * Look up help content by topic key. Returns null if the topic is not
 * registered — the consuming component should render nothing in that case.
 *
 * FUTURE: Replace this function body with a cached fetch to a CMS or
 * backend endpoint. No consumer changes required.
 */
export function getMetricHelp(topicKey) {
  return REGISTRY[topicKey] || null;
}

export function listMetricTopics() {
  return Object.keys(REGISTRY);
}
